const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * 서버 오류 문구를 사람이 읽게(2026-10-08, #107). 입력 검사(class-validator)는 영어 배열로 온다 —
 * 「manpower.0.headcount must not be less than 0」 → 「headcount: 0 이상이어야 합니다」
 */
/** 입력 칸 이름 — 영어 필드명 대신 화면에 쓰는 말 */
const FIELD: Record<string, string> = {
  headcount: '인원', monthsPerPerson: '개월', monthlyRate: '월 단가', proposalPrice: '제안가', grade: '등급',
  travel: '여비', outsourcing: '외주', etc: '기타 경비', contractAmount: '계약금액', contractValue: '계약금액(만원)',
  name: '이름', clientName: '고객사', dueAt: '마감', memo: '메모', status: '상태', content: '내용', title: '제목',
  key: 'API 키', label: '이름', model: '모델', kind: '종류', startDate: '시작일', endDate: '종료일', dueDate: '예정일', pm: 'PM',
};

export function humanizeError(message: unknown, status: number): string {
  const one = (m: string): string => {
    const r = m.match(/^([\w.]+) (.+)$/);
    if (!r) return m;
    const raw = r[1].split('.').filter((x) => !/^\d+$/.test(x)).pop() ?? r[1];
    const field = FIELD[raw] ?? raw;
    const rule = r[2];
    const t =
      rule.match(/^must not be less than (\S+)/) ? `${rule.match(/^must not be less than (\S+)/)![1]} 이상이어야 합니다`
      : rule.match(/^must not be greater than (\S+)/) ? `${rule.match(/^must not be greater than (\S+)/)![1]} 이하여야 합니다`
      : /must be an integer/.test(rule) ? '정수여야 합니다'
      : /must be a number/.test(rule) ? '숫자여야 합니다'
      : /must be longer than or equal to 1 /.test(rule) || /should not be empty/.test(rule) ? '비어 있으면 안 됩니다'
      : rule.match(/must be shorter than or equal to (\d+)/) ? `${rule.match(/must be shorter than or equal to (\d+)/)![1]}자 이하여야 합니다`
      : /must be one of the following values/.test(rule) ? '고를 수 있는 값이 아닙니다'
      : /must be a valid ISO 8601 date|must be a Date/.test(rule) ? '날짜 형식이 아닙니다'
      : /must be a string|must be a boolean|must be an array|must be an object/.test(rule) ? '형식이 맞지 않습니다'
      : /should not exist/.test(rule) ? '보낼 수 없는 값입니다'
      : null;
    return t ? `${field}: ${t}` : m;
  };
  if (Array.isArray(message)) return Array.from(new Set(message.map((m) => one(String(m))))).join(' · ');
  if (typeof message === 'string' && message) return one(message);
  return status >= 500 ? `서버 오류가 났습니다 (${status}). 잠시 뒤 다시 해 주세요` : `요청을 처리하지 못했습니다 (${status})`;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = localStorage.getItem('accessToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  private async handleResponse(response: Response) {
    if (response.status === 401) {
      // 토큰 갱신 시도
      const refreshRes = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: this.getHeaders(),
      });
      if (refreshRes.ok) {
        const data = await refreshRes.json();
        localStorage.setItem('accessToken', data.accessToken);
        return null; // 재시도 필요
      }
      localStorage.removeItem('accessToken');
      window.location.href = '/login';
      throw new Error('인증이 만료되었습니다.');
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(humanizeError(error.message, response.status));
    }
    return response.json();
  }

  async get(path: string) {
    const response = await fetch(`${this.baseUrl}${path}`, {
      headers: this.getHeaders(),
    });
    const result = await this.handleResponse(response);
    if (result === null) {
      // 재시도
      const retry = await fetch(`${this.baseUrl}${path}`, {
        headers: this.getHeaders(),
      });
      return this.handleResponse(retry);
    }
    return result;
  }

  /** 파일 — JSON 이 아니라 Blob 으로 받는다(원본 PDF 를 화면에서 열 때) */
  async getBlob(path: string): Promise<Blob> {
    const res = await fetch(`${this.baseUrl}${path}`, { headers: this.getHeaders() });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(humanizeError(error.message, res.status));
    }
    return res.blob();
  }

  async post(path: string, body?: any) {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse(response);
  }

  async put(path: string, body?: any) {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse(response);
  }

  async patch(path: string, body?: any) {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse(response);
  }

  /** 파일 올리기 — multipart. Content-Type 은 브라우저가 경계(boundary)까지 붙이게 비운다 */
  async postForm(path: string, form: FormData) {
    const headers = this.getHeaders();
    delete headers['Content-Type'];
    const response = await fetch(`${this.baseUrl}${path}`, { method: 'POST', headers, body: form });
    return this.handleResponse(response);
  }

  /** multipart 로 보내고 파일로 받는다 — PPT 자동 작성(#132) */
  async postFormBlob(path: string, form: FormData): Promise<Blob> {
    const headers = this.getHeaders();
    delete headers['Content-Type'];
    const res = await fetch(`${this.baseUrl}${path}`, { method: 'POST', headers, body: form });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(humanizeError(error.message, res.status));
    }
    return res.blob();
  }

  async delete(path: string) {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
